// FORFEO INC. - Lot 4R-B
// Contrat pur des calendriers marchands Forfeo Reserve.
// Reserve reste l'autorite de disponibilite; Giftshop reste financier.

import {
  FORFEO_RESERVE_AUTHORITIES,
  hasForfeoReserveForbiddenClientFields,
  isForfeoReserveHalfOpenInterval,
  isForfeoReserveIanaTimezone,
  normalizeForfeoReserveIdempotencyKey,
  type ForfeoReserveInstantWindow,
} from './forfeo-reserve-availability.ts';

export const FORFEO_RESERVE_CALENDAR_CONTRACT_VERSION =
  'forfeo-reserve-calendar-4r-b-v1' as const;

export const FORFEO_RESERVE_CALENDAR_VIEWS = [
  'day',
  'week',
  'month',
] as const;

export type ForfeoReserveCalendarView =
  (typeof FORFEO_RESERVE_CALENDAR_VIEWS)[number];

export const FORFEO_RESERVE_CALENDAR_SCOPES = [
  'establishment',
  'service',
  'resource',
  'employee',
] as const;

export type ForfeoReserveCalendarScope =
  (typeof FORFEO_RESERVE_CALENDAR_SCOPES)[number];

export const FORFEO_RESERVE_CALENDAR_EVENT_KINDS = [
  'manual_block',
  'external_busy',
  'reservation',
  'hold',
] as const;

export type ForfeoReserveCalendarEventKind =
  (typeof FORFEO_RESERVE_CALENDAR_EVENT_KINDS)[number];

export const FORFEO_RESERVE_CALENDAR_COMMANDS = [
  'create_calendar',
  'bind_calendar',
  'create_manual_block',
  'move_manual_block',
  'cancel_manual_block',
  'import_ical_event',
] as const;

export type ForfeoReserveCalendarCommandKind =
  (typeof FORFEO_RESERVE_CALENDAR_COMMANDS)[number];

export interface ForfeoReserveCalendarContract {
  id: string;
  businessId: string;
  name: string;
  scope: ForfeoReserveCalendarScope;
  timezone: string;
  defaultView: ForfeoReserveCalendarView;
  weekStartsOn: number;
  slotMinutes: number;
  status: 'active' | 'inactive' | 'archived';
  revision: number;
}

export interface ForfeoReserveCalendarBindingContract {
  id: string;
  calendarId: string;
  businessId: string;
  scope: ForfeoReserveCalendarScope;
  catalogItemId?: string;
  resourceId?: string;
  label: string;
  primary: boolean;
}

export interface ForfeoReserveCalendarTargetContract {
  id: string;
  targetType: 'catalog_item' | 'resource';
  kind: string;
  label: string;
  timezone: string;
  active: boolean;
}

export interface ForfeoReserveCalendarEventContract
  extends ForfeoReserveInstantWindow {
  id: string;
  calendarId: string;
  businessId: string;
  targetType: 'catalog_item' | 'resource';
  targetId: string;
  kind: ForfeoReserveCalendarEventKind;
  status: 'active' | 'cancelled';
  title: string;
  reasonCode?: string;
  sourceSystem: string;
  sourceId?: string;
  revision: number;
}

export interface ForfeoReserveCalendarSnapshot {
  authority: 'RESERVE';
  businessId: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  calendars: readonly ForfeoReserveCalendarContract[];
  bindings: readonly ForfeoReserveCalendarBindingContract[];
  availableTargets: readonly ForfeoReserveCalendarTargetContract[];
  events: readonly ForfeoReserveCalendarEventContract[];
  generatedAt: string;
}

export interface ForfeoReserveCalendarCommand {
  kind: ForfeoReserveCalendarCommandKind;
  businessId: string;
  calendarId?: string;
  targetType?: 'catalog_item' | 'resource';
  targetId?: string;
  eventId?: string;
  startsAt?: string;
  endsAt?: string;
  timezone?: string;
  name?: string;
  scope?: ForfeoReserveCalendarScope;
  defaultView?: ForfeoReserveCalendarView;
  reasonCode?: string;
  expectedRevision?: number;
  sourceId?: string;
  idempotencyKey: string;
  correlationId: string;
}

export interface ForfeoReserveIcalEvent extends ForfeoReserveInstantWindow {
  uid: string;
  summary: string;
  sequence: number;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ICAL_DATE = /^(\d{4})(\d{2})(\d{2})$/;
const ICAL_DATE_TIME =
  /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/;

export function isForfeoReserveCalendarView(
  value: unknown,
): value is ForfeoReserveCalendarView {
  return FORFEO_RESERVE_CALENDAR_VIEWS.includes(
    value as ForfeoReserveCalendarView,
  );
}

export function isForfeoReserveCalendarScope(
  value: unknown,
): value is ForfeoReserveCalendarScope {
  return FORFEO_RESERVE_CALENDAR_SCOPES.includes(
    value as ForfeoReserveCalendarScope,
  );
}

export function isForfeoReserveCalendarCommandKind(
  value: unknown,
): value is ForfeoReserveCalendarCommandKind {
  return FORFEO_RESERVE_CALENDAR_COMMANDS.includes(
    value as ForfeoReserveCalendarCommandKind,
  );
}

export function forfeoReserveCalendarEventsOverlap(
  first: ForfeoReserveInstantWindow,
  second: ForfeoReserveInstantWindow,
): boolean {
  if (
    !isForfeoReserveHalfOpenInterval(first.startsAt, first.endsAt)
    || !isForfeoReserveHalfOpenInterval(second.startsAt, second.endsAt)
  ) {
    return false;
  }

  return Date.parse(first.startsAt) < Date.parse(second.endsAt)
    && Date.parse(second.startsAt) < Date.parse(first.endsAt);
}

export function validateForfeoReserveCalendarCommand(
  command: ForfeoReserveCalendarCommand,
): readonly string[] {
  const errors: string[] = [];

  if (!isForfeoReserveCalendarCommandKind(command.kind)) {
    errors.push('invalid_command_kind');
  }
  if (!command.businessId) {
    errors.push('business_id_required');
  }
  if (!normalizeForfeoReserveIdempotencyKey(command.idempotencyKey)) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) {
    errors.push('invalid_correlation_id');
  }
  if (hasForfeoReserveForbiddenClientFields(command)) {
    errors.push('financial_fields_forbidden');
  }

  if (command.kind === 'create_calendar') {
    if (!command.name?.trim() || command.name.trim().length > 200) {
      errors.push('invalid_calendar_name');
    }
    if (!isForfeoReserveCalendarScope(command.scope)) {
      errors.push('invalid_calendar_scope');
    }
    if (!isForfeoReserveIanaTimezone(command.timezone)) {
      errors.push('invalid_timezone');
    }
    if (!isForfeoReserveCalendarView(command.defaultView)) {
      errors.push('invalid_default_view');
    }
  }

  if (
    command.kind === 'bind_calendar'
    || command.kind === 'create_manual_block'
    || command.kind === 'import_ical_event'
  ) {
    if (!command.calendarId) errors.push('calendar_id_required');
    if (!['catalog_item', 'resource'].includes(command.targetType ?? '')) {
      errors.push('invalid_target_type');
    }
    if (!command.targetId) errors.push('target_id_required');
  }

  if (
    command.kind === 'create_manual_block'
    || command.kind === 'move_manual_block'
    || command.kind === 'import_ical_event'
  ) {
    if (!isForfeoReserveHalfOpenInterval(command.startsAt, command.endsAt)) {
      errors.push('invalid_event_window');
    }
  }

  if (
    command.kind === 'move_manual_block'
    || command.kind === 'cancel_manual_block'
  ) {
    if (!command.eventId) errors.push('event_id_required');
    if (
      !Number.isSafeInteger(command.expectedRevision)
      || Number(command.expectedRevision) < 1
    ) {
      errors.push('invalid_expected_revision');
    }
  }

  if (
    command.kind === 'import_ical_event'
    && (!command.sourceId || command.sourceId.length > 255)
  ) {
    errors.push('invalid_ical_uid');
  }

  return [...new Set(errors)];
}

export function getForfeoReserveCalendarViewRange(
  anchor: Date,
  view: ForfeoReserveCalendarView,
  weekStartsOn = 1,
): ForfeoReserveInstantWindow {
  if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || weekStartsOn > 6) {
    throw new Error('invalid_week_start');
  }

  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);

  if (view === 'day') {
    end.setDate(end.getDate() + 1);
  } else if (view === 'week') {
    const delta = (start.getDay() - weekStartsOn + 7) % 7;
    start.setDate(start.getDate() - delta);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 7);
  } else {
    start.setDate(1);
    end.setFullYear(start.getFullYear(), start.getMonth() + 1, 1);
  }

  return {
    startsAt: start.toISOString(),
    endsAt: end.toISOString(),
  };
}

function unfoldIcalLines(value: string): string[] {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n[ \t]/g, '')
    .split('\n')
    .map((line) => line.trimEnd());
}

function zonedWallTimeToUtc(
  fields: readonly number[],
  timezone: string,
): Date {
  if (!isForfeoReserveIanaTimezone(timezone)) {
    throw new Error('invalid_ical_timezone');
  }

  const [year, month, day, hour, minute, second] = fields;
  const desiredWallTime = Date.UTC(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
  );
  let candidate = desiredWallTime;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const parts = Object.fromEntries(
      formatter
        .formatToParts(new Date(candidate))
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]),
    );
    const representedWallTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    const correction = desiredWallTime - representedWallTime;
    candidate += correction;
    if (correction === 0) break;
  }

  return new Date(candidate);
}

function parseIcalInstant(
  rawValue: string,
  timezone?: string,
): Date {
  const dateMatch = rawValue.match(ICAL_DATE);
  if (dateMatch) {
    return new Date(Date.UTC(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3]),
    ));
  }

  const dateTimeMatch = rawValue.match(ICAL_DATE_TIME);
  if (!dateTimeMatch) throw new Error('invalid_ical_datetime');
  const fields = dateTimeMatch.slice(1, 7).map(Number);

  if (dateTimeMatch[7] === 'Z') {
    return new Date(Date.UTC(
      fields[0],
      fields[1] - 1,
      fields[2],
      fields[3],
      fields[4],
      fields[5],
    ));
  }

  if (!timezone) throw new Error('floating_ical_datetime_forbidden');
  return zonedWallTimeToUtc(fields, timezone);
}

function unescapeIcalText(value: string): string {
  return value
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

function escapeIcalText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function parseForfeoReserveIcal(
  input: string,
  maximumEvents = 500,
): readonly ForfeoReserveIcalEvent[] {
  if (typeof input !== 'string' || input.length > 512_000) {
    throw new Error('ical_payload_too_large');
  }

  const lines = unfoldIcalLines(input);
  const events: ForfeoReserveIcalEvent[] = [];
  let current: Record<string, { value: string; timezone?: string }> | null = null;

  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') {
      if (current) throw new Error('nested_ical_event');
      current = {};
      continue;
    }
    if (line === 'END:VEVENT') {
      if (!current) throw new Error('unexpected_ical_event_end');
      const uid = current.UID?.value?.trim();
      const starts = current.DTSTART;
      const ends = current.DTEND;
      if (!uid || uid.length > 255 || !starts || !ends) {
        throw new Error('invalid_ical_event');
      }
      const startsAt = parseIcalInstant(starts.value, starts.timezone);
      const endsAt = parseIcalInstant(ends.value, ends.timezone);
      if (!(startsAt.getTime() < endsAt.getTime())) {
        throw new Error('invalid_ical_event_window');
      }
      events.push({
        uid,
        summary: unescapeIcalText(current.SUMMARY?.value || 'Indisponible')
          .slice(0, 200),
        sequence: Math.max(0, Number(current.SEQUENCE?.value || 0) || 0),
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
      });
      if (events.length > maximumEvents) {
        throw new Error('ical_event_limit_exceeded');
      }
      current = null;
      continue;
    }
    if (!current || !line || line.startsWith('BEGIN:') || line.startsWith('END:')) {
      continue;
    }

    const separator = line.indexOf(':');
    if (separator <= 0) continue;
    const descriptor = line.slice(0, separator);
    const value = line.slice(separator + 1);
    const [rawName, ...parameters] = descriptor.split(';');
    const name = rawName.toUpperCase();
    if (!['UID', 'DTSTART', 'DTEND', 'SUMMARY', 'SEQUENCE'].includes(name)) {
      continue;
    }
    const timezone = parameters
      .find((parameter) => parameter.toUpperCase().startsWith('TZID='))
      ?.slice(5);
    current[name] = { value, timezone };
  }

  if (current) throw new Error('unterminated_ical_event');
  return events;
}

function toIcalUtc(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error('invalid_ical_export_date');
  return date.toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

export function serializeForfeoReserveIcal(
  calendarName: string,
  events: readonly ForfeoReserveCalendarEventContract[],
): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FORFEO INC.//Forfeo Reserve 4R-B//FR',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeIcalText(calendarName.slice(0, 200))}`,
  ];

  for (const event of events.filter((item) => item.status === 'active')) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${escapeIcalText(event.sourceId || `forfeo-${event.id}`)}`,
      `DTSTAMP:${toIcalUtc(new Date().toISOString())}`,
      `DTSTART:${toIcalUtc(event.startsAt)}`,
      `DTEND:${toIcalUtc(event.endsAt)}`,
      `SUMMARY:${escapeIcalText(event.title || 'Indisponible')}`,
      `SEQUENCE:${Math.max(0, event.revision - 1)}`,
      'TRANSP:OPAQUE',
      'END:VEVENT',
    );
  }

  lines.push('END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

export function assertForfeoReserveCalendarBoundary(): true {
  if (
    FORFEO_RESERVE_AUTHORITIES.availability !== 'RESERVE'
    || FORFEO_RESERVE_AUTHORITIES.financial !== 'GIFTSHOP'
  ) {
    throw new Error('forfeo_reserve_calendar_authority_invalid');
  }
  return true;
}

export function isForfeoReserveIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE.test(value);
}
